import m from '@bitstillery/mithril'
import {classes, unique_id} from '@bitstillery/common/lib/utils'
import {MithrilComponent} from '@bitstillery/mithril'
import {Tippy, content_props, tip} from '../tip/tip'
import {TipMemo} from '../tip/tip_memo'

import {Badge} from '../badge/badge'
import {svg} from './icons'

import type {Content, Instance as TippyInstance, Props as TippyProps} from 'tippy.js'
import type {CmsComponentMeta} from '@bitstillery/common/cms/catalog_meta'

export const iconMeta = {
    tier: 'inline',
    summary: 'Sprite-based SVG icon.',
    when_to_use: '`tip` adds client-only tooltip behavior — omit on SSR-critical paths.',
    props: "name: string (required) — Sprite icon name.\nsize?: 'xs' | 's' | 'd' | 'l' | 'xl' | 'xxl' | 'xxxl' (default 'd').\ntype?: 'default' | 'brand' | 'success' | 'info' | 'warning' | 'danger' | 'surface' — colour/theme.\nclassName?: string\nbadge?: string | number — count/label overlay.\ntip?: string — client-only tooltip; omit on SSR-critical paths.\nonclick?: (e) => void",
    example: `
    <Icon name="email" size="l" type="info" />
    `,
    variants: [
        {
            prop: 'badge',
            jsx: `
            <Icon badge={3} name="email" size="l" type="info" />
            `,
        },
    ],
} satisfies CmsComponentMeta

export interface IconAttrs {
    active?: boolean | number | (() => boolean) | undefined
    id?: string
    /** Tooltip content: string, JSX (Vnode), or function returning string or JSX; the function may have nothing to say. */
    tip?: string | m.Vnode | null | (() => string | m.Vnode | null | undefined) | undefined
    tip_attrs?: Partial<TippyProps>
    /** Optional badge overlay showing a count or label (e.g. notification count) */
    badge?: string | number | undefined
    className?: string
    context?: string
    name: string
    size?: 'xs' | 's' | 'd' | 'l' | 'xl' | 'xxl' | 'xxxl' | undefined
    type?: 'default' | 'brand' | 'success' | 'info' | 'warning' | 'danger' | 'unset' | 'surface' | (string & {}) | undefined
    variant?: string
    disabled?: boolean | undefined
    onclick?: (event: MouseEvent) => unknown
}

/**
 * Tippy draws a null tip as an empty one, but its `Content` type leaves null out; an empty string draws
 * the same. Undefined stays: `setProps` skips an undefined prop, keeping the previous tip.
 */
function tippy_content(content: string | null | undefined): Content | undefined {
    return content === null ? '' : content
}

export class Icon extends MithrilComponent<IconAttrs> {
    id: string
    tippy: TippyInstance | null = null
    /**
     * Skips tippy when the tip has not changed. Worth more here than on Button: for a JSX tip,
     * `update_tip` renders the vnode into a throwaway div via `m.render` just to get an HTML string —
     * a whole nested render, previously on every redraw.
     */
    private tip_memo = new TipMemo<Content | undefined>()

    constructor(vn: m.Vnode<IconAttrs>) {
        super()
        if (vn.attrs.id) {
            this.id = vn.attrs.id
        } else {
            this.id = unique_id(8, false)
        }
    }

    override onremove(vn: m.Vnode<IconAttrs>) {
        if (vn.attrs.tip && this.tippy) {
            this.tippy.destroy()
        }
    }

    override oncreate(vn: m.Vnode<IconAttrs>) {
        if (vn.attrs.tip) {
            const tip_attrs: Partial<TippyProps> = {
                allowHTML: true,
                animation: 'scale',
                appendTo: document.body,
                ...content_props(tippy_content(this.update_tip(vn))),
                delay: 500,
                inertia: true,
                interactive: true,
                onTrigger: (instance: TippyInstance) => {
                    instance.setProps(content_props(tippy_content(this.update_tip(vn))))
                },
                touch: 'hold' as const,
                zIndex: 10000000000000000,
            }
            if (vn.attrs.tip_attrs) {
                Object.assign(tip_attrs, vn.attrs.tip_attrs)
            }
            this.tip_memo.prime(vn.attrs.tip, tip_attrs.content)
            const element = document.querySelector(`#${this.id}`)
            if (element) {
                this.tippy = tip(element, tip_attrs)
            }
        }
    }

    override onupdate(vn: m.Vnode<IconAttrs>) {
        if (!this.tippy) {
            return
        }
        const next = this.tip_memo.next(vn.attrs.tip, () => tippy_content(this.update_tip(vn)))
        if (next) {
            this.tippy.setProps(content_props(next.content))
        }
    }

    update_tip(vn: m.Vnode<IconAttrs>): string | null | undefined {
        const tip = vn.attrs.tip
        if (typeof tip === 'function') {
            const result = tip()
            // If the tip is a Mithril VNode (JSX), render it to HTML
            if (result && typeof result === 'object' && 'tag' in result) {
                const temp_div = document.createElement('div')
                m.render(temp_div, result)
                return temp_div.innerHTML
            }
            return result
        }
        // Handle direct JSX elements
        if (tip && typeof tip === 'object' && 'tag' in tip) {
            const temp_div = document.createElement('div')
            m.render(temp_div, tip)
            return temp_div.innerHTML
        }
        // Handle regular text/string tips
        return tip
    }

    view(vn: m.Vnode<IconAttrs>) {
        const tip_content = this.update_tip(vn)
        let element: m.Vnode = (
            <svg
                className={classes(
                    'c-icon',
                    vn.attrs.className,
                    vn.attrs.size ? `icon-${vn.attrs.size}` : 'icon-d',
                    vn.attrs.type ? `type-${vn.attrs.type}` : 'type-default',
                    {
                        action: vn.attrs.onclick,
                        active: typeof vn.attrs.active === 'function' ? vn.attrs.active() : vn.attrs.active,
                        disabled: vn.attrs.disabled,
                        tip: vn.attrs.tip && tip_content,
                        'tip-empty': vn.attrs.tip && !tip_content,
                    },
                )}
                id={this.id}
                onclick={(e: MouseEvent) => {
                    if (vn.attrs.disabled || !vn.attrs.onclick) return
                    vn.attrs.onclick(e)
                }}
                height='24'
                viewBox='0 0 24 24'
                width='24'
            >
                {m.trust(svg[vn.attrs.name] ?? '')}
            </svg>
        )

        if (vn.attrs.badge) {
            element = (
                <span className={classes('c-notification-icon', vn.attrs.size && `icon-${vn.attrs.size}`)}>
                    {element}
                    <Badge className='c-icon-badge' type='brand'>
                        {vn.attrs.badge}
                    </Badge>
                </span>
            )
        }

        if (vn.attrs.context) {
            return <Tippy content={vn.attrs.context}>{element}</Tippy>
        }

        return element
    }
}
